import type { ReactNode } from 'react'

// CheckboxGroup — ATC-128.
//
// A `<fieldset>` / `<legend>` rather than a `Field`, because a `<label>` may be
// associated with exactly ONE labelable control: wrapping several checkboxes in
// one label associates the text with the first and silently orphans the rest.
// Each box gets its own `<label>`; the set gets a `<legend>`.

interface CheckboxOptionBase {
  value: string
  label: ReactNode
}

/**
 * A DISABLED option MUST carry a `hint`, enforced here in the type rather than
 * at runtime.
 *
 * A greyed-out box with no stated reason is indistinguishable from a bug: the
 * operator cannot tell whether the option is forbidden, unavailable, or broken,
 * and the only recourse is to ask someone. On this console the disabled cases
 * are real rules with real reasons -- `ppkg` is grantable on `flightdeck`-class
 * rows only (`ATC-ADR-016`) -- so the reason always exists and there is no
 * excuse for not showing it.
 */
export type CheckboxOption =
  | (CheckboxOptionBase & { disabled?: false; hint?: ReactNode })
  | (CheckboxOptionBase & { disabled: true; hint: ReactNode })

export interface CheckboxGroupProps {
  legend: ReactNode
  options: CheckboxOption[]
  /** The currently-checked values. */
  value: string[]
  onChange: (next: string[]) => void
  /** Disables every option at once, e.g. while a request is in flight. */
  disabled?: boolean
}

export function CheckboxGroup({
  legend,
  options,
  value,
  onChange,
  disabled = false,
}: CheckboxGroupProps) {
  function toggle(optionValue: string, checked: boolean) {
    if (checked) {
      if (!value.includes(optionValue)) {
        onChange([...value, optionValue])
      }
      return
    }
    onChange(value.filter((v) => v !== optionValue))
  }

  return (
    <fieldset className="rx-checkgroup">
      <legend className="rx-checkgroup-legend">{legend}</legend>
      {options.map((option) => (
        <label className="rx-check" key={option.value}>
          <input
            type="checkbox"
            className="rx-check-input"
            checked={value.includes(option.value)}
            disabled={disabled || option.disabled === true}
            onChange={(e) => toggle(option.value, e.target.checked)}
          />
          <span className="rx-check-body">
            <span className="rx-check-label">{option.label}</span>
            {option.hint !== undefined && <span className="rx-check-hint">{option.hint}</span>}
          </span>
        </label>
      ))}
    </fieldset>
  )
}
